export const createImage = url =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export default async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
) {
  try {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    const rotRad = getRadianAngle(rotation);

    // calculate bounding box of the rotated image
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.width,
      image.height,
      rotation,
    );

    // set canvas size to match the bounding box
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    // translate canvas context to a central location to allow rotating and flipping around the center
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-image.width / 2, -image.height / 2);

    // draw rotated image
    ctx.drawImage(image, 0, 0);

    // croppedAreaPixels values are bounding box relative
    // extract the cropped image using these values
    const data = ctx.getImageData(
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
    );

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // paste generated rotate image at the top left corner
    ctx.putImageData(data, 0, 0);

    // As Base64 string
    const img = new Image();
    const newImage = (img.src = canvas.toDataURL('image/jpeg'));
    return newImage;
  } catch (e) {
    console.log(e);
  }

  // As a blob
  // return new Promise((resolve, reject) => {
  //   canvas.toBlob((file) => {
  //     resolve(URL.createObjectURL(file))
  //   }, 'image/png')
  // })
}

export const generateDownload = async (imageSrc, crop) => {
  if (!crop || !imageSrc) {
    return;
  }

  const canvas = await getCroppedImg(imageSrc, crop);

  canvas.toBlob(
    blob => {
      const previewUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement('a');
      anchor.download = 'image.jpeg';
      anchor.href = URL.createObjectURL(blob);
      anchor.click();

      window.URL.revokeObjectURL(previewUrl);
    },
    'image/jpeg',
    0.66,
  );
};

// export const getImageSrc = async () => {
//   const canvas = await getCroppedImg(image, croppedArea);
//   const canvasDataUrl = canvas.toDataURL("image/jpeg");
//   const convertedUrlToFile = dataURLtoFile(
//     canvasDataUrl,
//     "cropped-image.jpeg"
//   );
// };

// export const dataURLtoFile = (dataurl, filename) => {
// 	const arr = dataurl.split(",");
// 	const mime = arr[0].match(/:(.*?);/)[1];
// 	const bstr = atob(arr[1]);
// 	let n = bstr.length;
// 	const u8arr = new Uint8Array(n);

// 	while (n--) u8arr[n] = bstr.charCodeAt(n);

// 	return new File([u8arr], filename, { type: mime });
// };

export function getCroppedImgN(
  imageBase64: string,
  crop: any,
): Promise<File | null> {
  return new Promise(resolve => {
    const image = new Image();

    image.src = imageBase64;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height,
        );

        // Convert the canvas to a Blob
        canvas.toBlob(blob => {
          if (blob) {
            const mimeType = blob?.type ? blob?.type : 'image/jpeg';
            const fileType = mimeType.split('/')[1]; // Extracts 'jpeg'
            // Create a File from the Blob
            const file = new File(
              [blob],
              `${new Date().getTime()}_${Math.floor(
                new Date().getTime() / 1000,
              )}_croppedImage.${fileType}`,
              {
                type: blob?.type,
              },
            );
            resolve(file);
          } else {
            resolve(null);
          }
        }, 'image/jpeg');
      } else {
        resolve(null);
      }
    };

    image.onerror = () => {
      resolve(null);
    };
  });
}
